/*
 * Copyright (c) 2026 Joseph Duggan. All rights reserved.
 */

import { appPalette } from '@sidekick/design-tokens';
import { useEffect, useRef, useState } from 'react';
import { GOOGLE_OAUTH_STATE_KEY, createGoogleOauthState } from '../lib/googleOauth.js';
import { monoLabel } from '../lib/ui.js';
import { trpc } from '../lib/trpc.js';

const p = appPalette;

/**
 * Landing page for Google's OAuth redirect (/oauth/google/callback).
 *
 * Not a nav destination — App.tsx renders it instead of the shell when
 * the path matches at boot (CloudFront's SPA fallback serves index.html
 * for the path). Verifies the round-tripped `state` against the CSRF
 * nonce stashed in sessionStorage before the redirect, exchanges the
 * code server-side via calendar.completeGoogleOauth, then calls
 * `onDone` so the shell takes over on The Account.
 */
export function GoogleOauthCallbackPage({ onDone }: { onDone: (connected: boolean) => void }) {
  const complete = trpc.calendar.completeGoogleOauth.useMutation();
  const connect = trpc.calendar.connectGoogleCalendar.useMutation({
    onSuccess: ({ authUrl }) => {
      window.location.assign(authUrl);
    },
  });
  const fired = useRef(false);
  const [stateError, setStateError] = useState<string | null>(null);

  useEffect(() => {
    // Guard against React 18+ StrictMode double-mount — the code is
    // single-use, so the exchange must fire exactly once.
    if (fired.current) return;
    fired.current = true;

    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const state = params.get('state');
    const expected = sessionStorage.getItem(GOOGLE_OAUTH_STATE_KEY);
    const googleError = params.get('error');

    if (googleError != null) {
      setStateError(`Google declined the connection (${googleError}).`);
      return;
    }
    if (code == null || state == null) {
      setStateError('Google returned without a code. The connection was not made.');
      return;
    }
    if (expected == null || state !== expected) {
      // Possible CSRF, or the browser session changed mid-flow (e.g. a
      // sign-in happened between redirect and callback). Never exchange
      // the code in that case.
      setStateError('The security check on this connection failed. The connection was not made.');
      return;
    }

    complete.mutate(
      { code },
      {
        onSuccess: () => {
          sessionStorage.removeItem(GOOGLE_OAUTH_STATE_KEY);
          onDone(true);
        },
      },
    );
  }, [complete, onDone]);

  const error = stateError ?? complete.error?.message ?? null;

  return (
    <div
      style={{
        minHeight: '100vh',
        background: p.bg,
        color: p.fg,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: 22,
      }}
    >
      <div
        style={{ width: '100%', maxWidth: 520, display: 'flex', flexDirection: 'column', gap: 14 }}
      >
        <span style={monoLabel}>Google Calendar</span>
        {error != null ? (
          <>
            <p style={paragraphStyle}>{error}</p>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
              <button
                type="button"
                aria-label="Try connecting again"
                onClick={() => connect.mutate({ state: createGoogleOauthState() })}
                disabled={connect.isPending}
                style={{ ...buttonStyle, cursor: connect.isPending ? 'wait' : 'pointer' }}
              >
                {connect.isPending ? 'Opening Google…' : 'Try again →'}
              </button>
              <button
                type="button"
                onClick={() => onDone(false)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: p.muted,
                  fontSize: 13,
                  cursor: 'pointer',
                }}
              >
                Back to The Account
              </button>
            </div>
            {connect.error != null ? (
              <span style={{ ...monoLabel, color: p.accent }}>{connect.error.message}</span>
            ) : null}
          </>
        ) : (
          <p style={paragraphStyle}>Completing the connection…</p>
        )}
      </div>
    </div>
  );
}

const paragraphStyle: React.CSSProperties = {
  fontFamily: 'var(--font-serif)',
  fontFeatureSettings: 'var(--serif-feat)',
  fontSize: 15,
  lineHeight: 1.55,
  color: p.fg,
  margin: 0,
};

const buttonStyle: React.CSSProperties = {
  padding: '10px 18px',
  background: 'transparent',
  color: p.fg,
  border: `1px solid ${p.line}`,
  fontSize: 13,
};
